import { getTg } from "./tg";
import type { Me, Manager, Kpi, Period, Lang, AccessGrant, XlsxUpload, UploadResult, MonthStatus } from "./types";

const API_BASE = import.meta.env.VITE_API_BASE || "";

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const initData = getTg()?.initData ?? "";
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      "X-Init-Data": initData,
      ...(init.body ? { "Content-Type": "application/json" } : {}),
      ...init.headers,
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({} as any));
    const err = new Error((body as any).error || `HTTP_${res.status}`);
    if ((body as any).error === "MAINTENANCE") {
      (err as any).maintenance = {
        message_ru: (body as any).message_ru || "",
        message_uk: (body as any).message_uk || "",
      };
    }
    throw err;
  }
  return res.json() as Promise<T>;
}

export const apiGet   = <T>(path: string) => call<T>(path);
export const apiPatch = <T>(path: string, body: unknown) => call<T>(path, { method: "PATCH", body: JSON.stringify(body) });
export const apiPost  = <T>(path: string, body: unknown) => call<T>(path, { method: "POST",  body: JSON.stringify(body) });
export const apiDelete = <T>(path: string) => call<T>(path, { method: "DELETE" });

export const getMe       = () => apiGet<Me>("/api/me");
export const getManagers = () => apiGet<{ managers: Manager[] }>("/api/managers");
export const getKpi      = (managerId: string, period: Period, periodId: string) =>
  apiGet<{ kpi: Kpi }>(`/api/kpi?manager_id=${encodeURIComponent(managerId)}&period=${period}&period_id=${encodeURIComponent(periodId)}`);
export const patchLang   = (lang: Lang) => apiPatch<{ ok: true; lang: Lang }>("/api/me/lang", { lang });

export const getAccess = () => apiGet<{ grants: AccessGrant[] }>("/api/access");
export const createAccess = (phone: string, managerId: string) =>
  apiPost<{ grant: AccessGrant }>("/api/access", { phone, manager_id: managerId });
export const deleteAccess = (id: number) => apiDelete<{ ok: true }>(`/api/access/${id}`);
export const getUploads = () => apiGet<{ uploads: XlsxUpload[] }>("/api/uploads");

export interface RankEntry { manager_id: string; sales_per_day: number; returns_pct: number; }
export const getRanking = (period: Period, periodId: string) =>
  apiGet<{ entries: RankEntry[] }>(`/api/ranking?period=${period}&period_id=${encodeURIComponent(periodId)}`);

export const getAvailableMonths = () => apiGet<{ months: string[]; statuses?: MonthStatus[] }>("/api/months");

export const getAllManagers = () => apiGet<{ managers: Manager[] }>("/api/managers?include_archived=1");
export const createManager = (id: string, fullName: string) =>
  apiPost<{ manager: Manager }>("/api/managers", { id, full_name: fullName });
export const archiveManager = (id: string, active: boolean) =>
  apiPatch<{ ok: true; id: string; active: 0 | 1 }>(`/api/managers/${encodeURIComponent(id)}`, { active });

export type Settings = Record<string, string>;
export const getSettings = () => apiGet<{ settings: Settings }>("/api/settings");
export const patchSettings = (patch: Partial<Settings>) =>
  apiPatch<{ ok: true }>("/api/settings", patch);

export interface AccessEvent {
  id: number;
  user_id: number | null;
  phone: string | null;
  event_type: "login_ok" | "login_denied";
  at: number;
  meta: string | null;
  user_phone: string | null;
  first_name: string | null;
  last_name: string | null;
  role: string | null;
}
export const getEvents = (limit = 100) =>
  apiGet<{ events: AccessEvent[] }>(`/api/events?limit=${limit}`);

export async function uploadXlsx(file: File): Promise<UploadResult> {
  const initData = (window as any).Telegram?.WebApp?.initData ?? "";
  const buf = await file.arrayBuffer();
  const res = await fetch(`${API_BASE}/api/upload`, {
    method: "POST",
    headers: {
      "X-Init-Data": initData,
      "Content-Type": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "X-Filename": file.name,
    },
    body: buf,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({} as any));
    throw new Error((body as any).error || `HTTP_${res.status}`);
  }
  return res.json() as Promise<UploadResult>;
}
